import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, ExternalLink, AlertCircle, FileText, ShieldCheck } from 'lucide-react';

const API = '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * In-app preview modal for an uploaded evidence file. Fetches the inline
 * presigned URL and renders it natively for PDFs / images / audio / video
 * / text. Layered above the Control Detail modal — Radix bumps z-index
 * automatically when nested dialogs open.
 */
const FilePreviewDialog = ({ open, onClose, file }) => {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [previewType, setPreviewType] = useState('inline');
  const [error, setError] = useState(null);
  // After the backend renders office docs to PDF the effective MIME for
  // display can differ from the source file's MIME. Track it separately.
  const [renderedMime, setRenderedMime] = useState(null);
  const [rendered, setRendered] = useState(false);
  // Client-side .docx renderer state (mammoth). Runs entirely in-browser so
  // the bytes never leave the customer's tenant.
  const [docxHtml, setDocxHtml] = useState(null);
  const [docxLoading, setDocxLoading] = useState(false);
  const [docxError, setDocxError] = useState('');

  useEffect(() => {
    if (!open || !file?.id) { setUrl(null); setEmbedUrl(null); setError(null); return; }
    let cancelled = false;
    const fetchUrl = async () => {
      setLoading(true);
      setError(null);
      setUrl(null);
      setEmbedUrl(null);
      setRenderedMime(null);
      setRendered(false);
      setPreviewType('inline');
      try {
        const res = await axios.get(`${API}/files/preview/${file.id}`, authHeaders());
        if (cancelled) return;
        setUrl(res.data?.download_url || null);
        setEmbedUrl(res.data?.embed_url || null);
        setPreviewType(res.data?.preview_type || 'inline');
        setRenderedMime((res.data?.preview_mime || '').toLowerCase());
        setRendered(!!res.data?.rendered);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message || 'Failed to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [open, file?.id]);

  const mime = renderedMime || (file?.mime_type || '').toLowerCase();
  const fname = (file?.filename || '').toLowerCase();
  const isDocx =
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    fname.endsWith('.docx');
  const isMsOffice = previewType === 'ms_office_online' && !!embedUrl && !isDocx;
  const isImage = !isMsOffice && !isDocx && mime.startsWith('image/');
  const isVideo = !isMsOffice && !isDocx && mime.startsWith('video/');
  const isAudio = !isMsOffice && !isDocx && mime.startsWith('audio/');
  // PDFs and text-likes render fine in an iframe (browser handles them).
  const useIframe = !isMsOffice && !isDocx && !isImage && !isVideo && !isAudio;

  // Fetch the .docx bytes via our backend streaming endpoint (guaranteed
  // CORS + auth) and convert to HTML using mammoth in the browser. Falls
  // back to Office Online only if this fails.
  useEffect(() => {
    let cancelled = false;
    if (!open || !isDocx || !file?.id) return undefined;
    setDocxHtml(null);
    setDocxError('');
    setDocxLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API}/files/${file.id}/raw`, {
          credentials: 'omit',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!resp.ok) throw new Error(`Failed to fetch document (${resp.status})`);
        const arrayBuffer = await resp.arrayBuffer();
        const mod = await import('mammoth/mammoth.browser');
        const mammoth = mod.default || mod;
        const result = await mammoth.convertToHtml({ arrayBuffer });
        if (!cancelled) setDocxHtml(result.value || '<p>(empty document)</p>');
      } catch (e) {
        if (!cancelled) setDocxError(e.message || 'Could not render document inline');
      } finally {
        if (!cancelled) setDocxLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isDocx, file?.id]);

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/files/download/${file.id}`, authHeaders());
      if (res.data?.download_url) window.open(res.data.download_url, '_blank', 'noopener,noreferrer');
    } catch (_e) {
      // toast handled by FileList — silent here is fine
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[90vh] p-0 flex flex-col overflow-hidden [&>button.absolute]:hidden"
        data-testid="file-preview-dialog"
      >
        <DialogTitle className="sr-only">{file?.filename || 'File preview'}</DialogTitle>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-navy-600 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-800 truncate" title={file?.filename}>
              {file?.filename || 'Preview'}
            </span>
            {(rendered || isDocx) && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-navy-700 bg-navy-50 border border-navy-200 rounded px-1.5 py-0.5 flex-shrink-0"
                title={
                  isDocx
                    ? 'Rendered inline in your browser — the document bytes never leave your Nova tenant.'
                    : isMsOffice
                    ? 'Rendered via Microsoft Office Online — the file is fetched by Microsoft to render the preview.'
                    : 'Rendered inline via signed URL.'
                }
                data-testid="file-preview-rendered-badge"
              >
                <ShieldCheck className="w-3 h-3" />{' '}
                {isDocx ? 'Rendered on Nova' : isMsOffice ? 'MS Office Online' : 'Rendered on Nova'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {url && (
              <Button
                variant="outline"
                size="sm"
                asChild
                title="Open in new tab"
                data-testid="file-preview-open-new-tab"
              >
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              title="Download"
              data-testid="file-preview-download"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              data-testid="file-preview-close"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 bg-slate-100 overflow-hidden flex items-center justify-center">
          {loading && (
            <div className="flex flex-col items-center text-slate-500 text-sm gap-1">
              <div className="flex items-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading preview…
              </div>
              {(file?.mime_type || '').toLowerCase().match(/word|excel|powerpoint|openxmlformats|opendocument|rtf/) && (
                <span className="text-xs text-slate-400">First-time rendering of office docs can take a few seconds.</span>
              )}
            </div>
          )}
          {error && !loading && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3" data-testid="file-preview-error">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && (url || embedUrl) && (
            <>
              {isDocx && (
                <div className="w-full h-full overflow-y-auto bg-white">
                  {docxLoading && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-500 text-sm">
                      <Loader2 className="w-5 h-5 animate-spin mb-2" />
                      Rendering document…
                    </div>
                  )}
                  {!docxLoading && docxHtml && (
                    <div
                      className="mx-auto max-w-4xl px-8 py-10 prose prose-slate max-w-none"
                      data-testid="file-preview-docx-html"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: docxHtml }}
                    />
                  )}
                  {!docxLoading && !docxHtml && docxError && !embedUrl && (
                    <div className="flex flex-col items-center justify-center h-full text-slate-600 gap-2 px-6 text-center">
                      <AlertCircle className="w-6 h-6 text-amber-500" />
                      <div className="text-sm font-medium">Preview unavailable</div>
                      <div className="text-xs text-slate-500 max-w-md">{docxError}</div>
                    </div>
                  )}
                  {!docxLoading && !docxHtml && docxError && embedUrl && (
                    // Legacy fallback: mammoth failed and server built an MS
                    // Office Online embed URL — surface that inline.
                    <iframe
                      src={embedUrl}
                      title={file?.filename || 'Office preview'}
                      className="w-full h-full bg-white"
                      data-testid="file-preview-office-iframe-fallback"
                    />
                  )}
                </div>
              )}
              {isMsOffice && (
                <iframe
                  src={embedUrl}
                  title={file?.filename || 'Office preview'}
                  className="w-full h-full bg-white"
                  data-testid="file-preview-office-iframe"
                />
              )}
              {isImage && (
                <img
                  src={url}
                  alt={file?.filename}
                  className="max-w-full max-h-full object-contain"
                  data-testid="file-preview-image"
                />
              )}
              {isVideo && (
                <video src={url} controls className="max-w-full max-h-full" data-testid="file-preview-video" />
              )}
              {isAudio && (
                <audio src={url} controls className="w-[80%]" data-testid="file-preview-audio" />
              )}
              {useIframe && (
                <iframe
                  src={url}
                  title={file?.filename || 'Preview'}
                  className="w-full h-full bg-white"
                  data-testid="file-preview-iframe"
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
